import { Controller } from "@hotwired/stimulus"

// A kit registration dropdown. The dropdown lists only the kits recommended for
// the visit; this controller adds everything else:
//
//  - typing any kit number offers a "Use …" option, looked up in the full
//    catalog (every kit that could be registered)
//  - a kit that isn't part of the visit asks for confirmation first
//  - a number that matches no kit is blocked with an error
//
// Once a kit is chosen it dispatches `kit-registration-select:selected` with
// { id, number, alreadyUsed } so the surrounding step can record it.
export default class extends Controller {
  static targets = ["toggle", "label", "typedOption", "error", "modal", "message"]
  static values = { catalog: Array, currentVisitName: String }

  disconnect() {
    this.resolveConfirmation?.(false)
  }

  // --- choosing a kit ------------------------------------------------------

  selectListed(event) {
    const id = event.currentTarget.dataset.entryValue
    this.choose(this.catalogValue.find((kit) => String(kit.id) === id))
  }

  selectTyped() {
    if (!this.typed) return
    if (this.typed.missing) return this.showNotFound(this.typed.missing)
    this.choose(this.typed.kit)
  }

  async choose(kit) {
    if (!kit) return
    if (kit.demandMismatch && !(await this.confirmMismatch(kit))) return

    this.labelTarget.textContent = kit.number
    this.dispatch("selected", { detail: { id: kit.id, number: kit.number, alreadyUsed: kit.alreadyUsed } })
  }

  // --- typed entry (driven by searchable-dropdown's events) ----------------

  filtered(event) {
    const { query, visibleCount, noResults } = event.detail
    this.visibleCount = visibleCount
    this.typed = this.lookup(query)

    // Kits recommended for the visit are already listed; offer the rest.
    const offer = this.typed && (this.typed.missing || this.typed.kit.demandMismatch)
    this.typedOptionTarget.classList.toggle("d-none", !offer)
    if (offer) {
      this.typedOptionTarget.textContent = `Use "${this.typed.kit?.number ?? this.typed.missing}"`
      // The typed option is the result, so "No results found" would mislead.
      noResults?.classList.add("d-none")
    }
  }

  // Enter picks a typed kit that exists — otherwise a fuzzy match on some other
  // kit would win and silently select the wrong one — or, for a number that
  // doesn't exist, when there's nothing else listed (so the error shows).
  enter(event) {
    if (!this.typed) return
    if (this.typed.missing && this.visibleCount > 0) return

    event.preventDefault()
    this.selectTyped()
  }

  lookup(query) {
    const typed = query.trim()
    if (typed === "") return null

    const lower = typed.toLowerCase()
    const kit = this.catalogValue.find((candidate) => candidate.number && candidate.number.toLowerCase() === lower)
    return kit ? { kit } : { missing: typed }
  }

  // --- not in the catalog --------------------------------------------------

  showNotFound(number) {
    this.errorTarget.textContent = `Kit "${number}" was not found among the kits available for registration and cannot be used.`
    this.errorTarget.classList.add("d-block")
    $(this.toggleTarget).dropdown("hide")
  }

  clearError() {
    this.errorTarget.classList.remove("d-block")
  }

  // --- mismatch confirmation -----------------------------------------------

  confirmMismatch(kit) {
    this.messageTarget.textContent = kit.mappedVisitName
      ? `This kit accession number is for the ${kit.mappedVisitName} visit, not ${this.currentVisitNameValue}. Do you still want to proceed with using this kit for this visit?`
      : "This kit accession number is for a kit that is not applicable to this visit. Please confirm you want to use this kit."

    return new Promise((resolve) => {
      this.resolveConfirmation?.(false)
      this.resolveConfirmation = resolve
      $(this.modalTarget).modal("show")
    })
  }

  confirm() {
    this.settle(true)
  }

  dismiss() {
    this.settle(false)
  }

  // Settle straight off the button clicks rather than waiting on Bootstrap's
  // hidden.bs.modal — this app's bundled jQuery modal plugin toggles the modal's
  // visibility classes but never dispatches that event, which would leave the
  // promise hanging forever.
  settle(confirmed) {
    $(this.modalTarget).modal("hide")
    this.resolveConfirmation?.(confirmed)
    this.resolveConfirmation = null
  }
}
